// Inside-the-Canvas consumer of `cameraInput.ts`: drains the minimap's drag requests
// once per frame and moves the orbit target AND camera by the same delta (so the view
// angle never changes — this is a pan, not a look). Stands down while a cinematic owns
// the camera (controls disabled), like CameraFlight / CameraPanClamp.

import { useFrame } from '@react-three/fiber';
import type { RefObject } from 'react';
import { getBoardScale } from '../boardScale.js';
import { cameraGrab, cancelCameraGrab, grabTarget, takeCameraInput } from './cameraInput.js';
import type { FlightControls } from './flight.js';

interface PanControls {
  readonly target: {
    x: number;
    y: number;
    z: number;
    set(x: number, y: number, z: number): unknown;
  };
  enabled: boolean;
}

export function CameraPanInput({
  controls,
}: {
  readonly controls: RefObject<(PanControls & FlightControls) | null>;
}) {
  useFrame(({ camera }) => {
    const input = takeCameraInput();
    const ctl = controls.current;
    if (!ctl) {
      cancelCameraGrab();
      return;
    }
    if (!ctl.enabled) {
      // A cinematic owns the camera: a grab mid-flight is dropped, not queued.
      cancelCameraGrab();
      return;
    }
    if (input && !cameraGrab.active) {
      // A new grab: remember where it started and the orbit target it started
      // from. Only the FIRST frame of a gesture captures the target — later
      // frames of the same gesture re-send the same begin, and re-capturing
      // would restart the grab math and leave the camera where it was.
      cameraGrab.active = true;
      cameraGrab.startX = input.begin[0];
      cameraGrab.startZ = input.begin[1];
      cameraGrab.targetX = ctl.target.x;
      cameraGrab.targetZ = ctl.target.z;
    }
    if (!cameraGrab.active) return;
    const current = input?.point ?? null;
    if (!current) return;
    const s = getBoardScale();
    const desired = grabTarget(
      [cameraGrab.startX * s, cameraGrab.startZ * s],
      [current[0] * s, current[1] * s],
      [cameraGrab.targetX, cameraGrab.targetZ],
    );
    const dx = desired[0] - ctl.target.x;
    const dz = desired[1] - ctl.target.z;
    if (dx === 0 && dz === 0) return;
    ctl.target.set(ctl.target.x + dx, ctl.target.y, ctl.target.z + dz);
    camera.position.x += dx;
    camera.position.z += dz;
  });

  return null;
}
